
import { useState } from "react";
import Image from "./Image";
import { ChevronRight, TicketPercent } from "lucide-react";
import { AboutRentid } from "./AboutRentid";
// INSHUR brand palette
const INSHUR_YELLOW = "#F6BD2C";
// Partnership CTA destinations — swap in the final URLs here.
/* Owner (2026-09-30): on rentid.io/inshur, "Claim Free Rentid Account" goes to the INSHUR partner sign-up
   in the app. Live since 2026-10-05; set INSHUR_SIGNUP_ENABLED to false to render every copy
   disabled again. */
export const GET_STARTED_URL = "https://app.rentid.io/sign-up?partner=inshur"; // v0: /onboarding
export const INSHUR_SIGNUP_ENABLED = true;
const DEFAULT_IMAGES = [
    {
        src: "/images/inshur-rentid-hero.png",
        alt: "INSHUR and Rentid partnership — a rental vehicle on the road with embedded commercial auto insurance",
        fit: "contain",
        position: "center",
    },
];
export function InshurHero({ images = DEFAULT_IMAGES } = {}) {
    const [active, setActive] = useState(0);
    const slides = images.length > 0 ? images : DEFAULT_IMAGES;
    const current = slides[active];
    const hasMultiple = slides.length > 1;
    const goNext = () => setActive((prev) => (prev + 1) % slides.length);
    return (<section className="relative min-h-screen flex flex-col">
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2">
        {/* Left column — headline, subcopy, CTAs */}
        <div className="order-1 flex flex-col justify-center px-5 sm:px-8 md:px-10 lg:px-16 py-14 sm:py-16 lg:py-20">
          {/* Eyebrow */}
  <p className="mb-6 flex items-center gap-3">
  <span className="sr-only">INSHUR x Rentid</span>
  <img src="/img/inshurlp/inshur-logo-blue.svg" alt="" aria-hidden="true" className="h-5 w-auto"/>
  <span aria-hidden="true" className="text-base text-muted-foreground">x</span>
  <img src="/logos/rentid-wordmark-black.svg" alt="" aria-hidden="true" className="h-5 w-auto -translate-y-0.5"/>
  </p>

          {/* H1 */}
          <h1 className="font-heading text-4xl sm:text-[48px] font-bold tracking-tighter leading-[1.05] text-foreground text-balance mb-4">
            Direct & Private Rental Platform with PZ built into the checkout.
          </h1>

          {/* Subcopy */}
          <p className="text-base text-[#202020] leading-relaxed text-pretty max-w-md mb-10">
            Manage and scale your rental business with greater control, including bookings, on rent insurance, driver verification, fleet management, payments, agreements, roadside, and more.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            {INSHUR_SIGNUP_ENABLED ? (<a href={GET_STARTED_URL} className="btn-pulse inline-flex items-center justify-center text-center px-7 py-4 sm:py-3.5 rounded-full text-base sm:text-sm font-semibold text-black transition-colors duration-200 hover:bg-[#2be0a8] active:scale-95" style={{ backgroundColor: "#33F7BE" }}>
              Claim Free Rentid Account
            </a>) : (<button type="button" disabled aria-disabled="true" title="Coming soon" data-href={GET_STARTED_URL} className="inline-flex cursor-not-allowed items-center justify-center text-center px-7 py-4 sm:py-3.5 rounded-full text-base sm:text-sm font-semibold text-black opacity-50" style={{ backgroundColor: "#33F7BE" }}>
              Claim Free Rentid Account
            </button>)}
            <AboutRentid />
          </div>

          {/* Pro plan promo badge */}
          <div className="mb-5 inline-flex w-fit items-center gap-3 rounded-xl bg-muted px-4 py-3">
            <TicketPercent className="size-5 shrink-0 text-foreground" aria-hidden="true"/>
            <p className="microcopy-shimmer text-sm font-semibold text-foreground" style={{ animationDuration: "10s" }}>
              Access Pro Plan Free for 30 Days
            </p>
          </div>
        </div>

        {/* Right column — image carousel with a next arrow */}
        <div className="order-2 flex flex-col justify-center items-center px-8 sm:px-12 lg:px-16 py-14 sm:py-16 lg:py-20">
          <div className="relative w-full max-w-md">
            {/* Decorative hatched circle layered behind the bottom-left corner */}
            <Image src="/img/inshurlp/hatched-circle-purple.png" alt="" aria-hidden="true" width={400} height={400} className="pointer-events-none absolute -bottom-10 -left-12 z-0 w-48 sm:w-60 h-auto select-none"/>
            {/* Decorative hatched circle layered behind the top-middle edge */}
            <Image src="/img/inshurlp/hatched-circle-yellow.png" alt="" aria-hidden="true" width={400} height={400} className="pointer-events-none absolute -top-10 left-3/4 -translate-x-1/2 z-0 w-40 sm:w-48 h-auto select-none"/>
            <Image src="/images/inshur-preferred-partner-2026.webp" alt="INSHUR Preferred Partner 2026" width={1920} height={820} className="pointer-events-none absolute -left-4 top-0 z-20 h-auto w-28 -translate-y-1/2 select-none drop-shadow-md sm:-left-6 sm:w-36"/>

            <div className="relative z-10 w-full aspect-square overflow-hidden rounded-3xl bg-[#dcd7ce]">
            <Image key={current.src} src={current.src || "/placeholder.svg"} alt={current.alt} width={1200} height={1200} className={`w-full h-full ${current.fit === "contain" ? "object-contain" : "object-cover"}`} style={{ objectPosition: current.position ?? "center" }} priority/>

            {hasMultiple && (<>
                {/* Next arrow */}
                <button type="button" onClick={goNext} aria-label="Show next image" className="absolute right-3 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-background/90 text-foreground shadow-md ring-1 ring-black/5 backdrop-blur transition-transform duration-200 hover:bg-background active:scale-95">
                  <ChevronRight className="h-5 w-5" aria-hidden="true"/>
                </button>

                {/* Dots */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2">
                  {slides.map((slide, i) => (<button key={slide.src} type="button" onClick={() => setActive(i)} aria-label={`Show image ${i + 1}`} aria-current={i === active} className={`h-2 rounded-full transition-all duration-200 ${i === active ? "w-6 bg-background" : "w-2 bg-background/50 hover:bg-background/70"}`}/>))}
                </div>
              </>)}
            </div>
          </div>
        </div>
      </div>
    </section>);
}
